import { Container, Divider } from '@cleartrip/ct-design-components'
import { motion } from 'framer-motion'
import React, { memo, useCallback } from 'react'

import { getSrpHeroTheme } from '@/constants/flights/premiumEconomy'
import { PaxCriteria } from '@/types/srp'

import type { JourneyLeg } from './config'
import { TUPLE_COLORS, TUPLE_HOVER_SHADOW, TUPLE_PRICE_ACTION_GAP } from './config/tupleTheme'
import type { TupleOneWayViewModel } from './model/types'
import AirlineBlock from './ui/AirlineBlock'
import DurationStopsBlock from './ui/DurationStopsBlock'
import OfferCallout from './ui/OfferCallout'
import PriceBlock from './ui/PriceBlock'
import TimeRouteBlock from './ui/TimeRouteBlock'
import TupleActions from './ui/TupleActions'
import TupleMetaRow from './ui/TupleMetaRow'

export interface TupleOneWayProps {
  card: TupleOneWayViewModel
  cabin?: string
  cardIndex?: number
  paxCriteria?: PaxCriteria[]
  onFlightDetails?: (card: TupleOneWayViewModel, leg: JourneyLeg) => void
  onBook?: (card: TupleOneWayViewModel) => void
  radius?: number
}

const CSS_OUTER = { minWidth: 0 }
const CSS_ROUTE = { width: 380, minWidth: 380, maxWidth: 380, flex: '0 0 380px', columnGap: 16 }
const CSS_PRICE_ACTION = { gap: TUPLE_PRICE_ACTION_GAP }

const TupleOneWay: React.FC<TupleOneWayProps> = ({
  card,
  cabin,
  cardIndex,
  paxCriteria,
  onFlightDetails,
  onBook,
  radius = 16,
}) => {
  const theme = getSrpHeroTheme(cabin)
  const handleDetails = useCallback(() => onFlightDetails?.(card, 'J1'), [card, onFlightDetails])
  const handleBook = useCallback(() => onBook?.(card), [card, onBook])

  const body = (
    <Container className='flex flex-column flex-rg-3 p-3 w-100p' css={CSS_OUTER}>
      <Container className='flex flex-between w-100p' css={CSS_OUTER}>
        <AirlineBlock airlines={card.airlines} flightNumbersLabel={card.flightNumbersLabel} />

        <Container className='flex flex-middle flex-no-shrink' css={CSS_ROUTE}>
          <TimeRouteBlock endpoint={card.departure} />
          <DurationStopsBlock durationLabel={card.durationLabel} stop={card.stop} />
          <TimeRouteBlock align='end' endpoint={card.arrival} />
        </Container>

        <Container className='flex flex-top flex-no-shrink' css={CSS_PRICE_ACTION}>
          <PriceBlock paxCriteria={paxCriteria} price={card.price} />
          <TupleActions variant='book' onBook={handleBook} />
        </Container>
      </Container>

      <TupleMetaRow
        tags={card.tags}
        trailing={
          <Container className='flex flex-middle flex-wrap flex-end' css={{ columnGap: 0, rowGap: 6 }}>
            <OfferCallout
              cardIndex={cardIndex}
              couponCallout={card.couponCallout}
              fareId={card.fareId}
              showPerAdult={card.price.showPerAdult}
              trailing={
                <Divider
                  borderColor={TUPLE_COLORS.metaDivider}
                  borderStyle='solid'
                  className='flex-no-shrink mx-2'
                  height='14px'
                  type='solid'
                  width='1px'
                />
              }
            />
            <TupleActions variant='details' onFlightDetails={handleDetails} />
          </Container>
        }
      />
    </Container>
  )

  if (theme) {
    return (
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className='p-relative w-100p'
        initial={{ opacity: 0, y: 8 }}
        style={{
          borderRadius: radius,
          padding: 1,
          background: theme.tupleBorderGradient,
        }}
        transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
        whileHover={{ boxShadow: TUPLE_HOVER_SHADOW }}
      >
        <Container
          className='flex flex-column o-hidden w-100p'
          style={{ backgroundColor: '#FFFFFF', borderRadius: radius - 1 }}
        >
          {body}
        </Container>
      </motion.div>
    )
  }

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className='p-relative flex flex-column bg-white o-hidden w-100p'
      initial={{ opacity: 0, y: 8 }}
      style={{
        borderRadius: radius,
        border: `1px solid ${TUPLE_COLORS.cardBorder}`,
      }}
      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
      whileHover={{ boxShadow: TUPLE_HOVER_SHADOW }}
    >
      {body}
    </motion.div>
  )
}

export default memo(TupleOneWay)
